import React, { useState, useEffect } from 'react';
import { getAuth, signOut } from 'firebase/auth';
import { auth, app, API_BASE_URL } from '../../firebase/firebaseConfig';
import { Button } from "../ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../ui/card";
import { Input } from "../ui/input";
import { LogOut } from "lucide-react";
import ErrorModal from '../ui/ErrorModal';

// Import visual assets
import { ReactComponent as WelcomeLogo } from '../../assets/setup-visuals/w_for_welcome.svg';
import { ReactComponent as HearthIcon } from '../../assets/setup-visuals/hearth_icon.svg';
import { ReactComponent as PrimaryHearthSymbol } from '../../assets/setup-visuals/primary_hearth_symbol.svg';
import { ReactComponent as AdditionalHearthSymbol } from '../../assets/setup-visuals/additional_hearth_symbol.svg';
import { ReactComponent as HomeSymbol } from '../../assets/setup-visuals/home_symbol.svg';
import { ReactComponent as AutoModeSymbol } from '../../assets/setup-visuals/auto_mode_symbol.svg';
import { ReactComponent as NormalModeSymbol } from '../../assets/setup-visuals/normal_mode_symbol.svg';
import HigloGraphics from '../../assets/setup-visuals/higlo_graphics.jpg';

function AccountSetup({ email, userType, familyIDs, familyName, numOfFamilies, nameFromServer, nameAtOrder, onSetupComplete }) {
  // Pre-populate name: first check nameFromServer (if exists), then nameAtOrder, otherwise empty
  const initialName = nameFromServer || nameAtOrder || '';
  const [name, setName] = useState(initialName);
  const [familyNames, setFamilyNames] = useState([]);
  const [iglooName, setIglooName] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [showErrorModal, setShowErrorModal] = useState(false);

  useEffect(() => {
    if (userType === 'invitedUser') {
      setFamilyNames([familyName]);
    } else {
      // For hearthOwners, use numOfFamilies to create empty family name slots
      setFamilyNames(Array(numOfFamilies).fill(''));
    }
  }, [userType, familyName, numOfFamilies]);

  const handleStep1Submit = (e) => {
    e.preventDefault();

    if (!name) {
      setErrorMessage('Please fill in your name.');
      setShowErrorModal(true);
      return;
    }

    // For invited users, we can complete setup after step 1
    if (userType === 'invitedUser') {
      handleFinalSubmit();
    } else {
      // For hearth owners, go to step 2 (Hearth Intro)
      setCurrentStep(2);
    }
  };

  const handleStep3Submit = (e) => {
    e.preventDefault();

    if (familyNames.some(f => !f)) {
      setErrorMessage('Please provide names for all Hearths.');
      setShowErrorModal(true);
      return;
    }

    // Go to Higlos Intro
    setCurrentStep(4);
  };

  const handleStep5Submit = (e) => {
    e.preventDefault();

    if (!iglooName.trim()) {
      setErrorMessage('Please provide a name for your first Higlo.');
      setShowErrorModal(true);
      return;
    }

    // Go to Modes Intro
    setCurrentStep(6);
  };

  const handleFinalSubmit = async () => {
    setIsLoading(true);

    if (!userType) {
      console.error('Error: userType during AccountSetup is null');
      setErrorMessage('An error occurred. Please try again.');
      setShowErrorModal(true);
      setIsLoading(false);
      return;
    }

    try {
      const currentUser = auth.currentUser;
      const idToken = await currentUser.getIdToken();

      // For hearthOwners, familyIDs are generated server-side
      // For invitedUsers, we need to send the familyIDs they're joining
      const requestBody = { email, name, familyNames, userType };
      if (userType === 'hearthOwner' && iglooName.trim()) {
        requestBody.iglooName = iglooName.trim();
      }
      if (userType === 'invitedUser') {
        requestBody.familyIDs = familyIDs;
      }

      const response = await fetch(`${API_BASE_URL}/user/setup`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`
        },
        body: JSON.stringify(requestBody),
      });

      const data = await response.json();
      if (data.success) {
        onSetupComplete();
      } else {
        setErrorMessage('Setup failed. Please try again.');
        setShowErrorModal(true);
      }
    } catch (error) {
      console.error('Error:', error);
      setErrorMessage('An error occurred. Please try again.');
      setShowErrorModal(true);
    } finally {
      setIsLoading(false);
    }
  };

  const closeErrorModal = () => {
    setShowErrorModal(false);
    setErrorMessage('');
  };

  const handleSignOut = async () => {
    const authInstance = getAuth(app);
    try {
      await signOut(authInstance);
      window.location.reload();
    } catch (err) {
      console.error('Error signing out:', err);
    }
  };

  // Get the progress segment (1, 2, or 3) for the current step
  const getProgressSegment = () => {
    if (currentStep <= 3) return 1; // Hearth intro + setup
    if (currentStep <= 5) return 2; // Higlos intro + name
    return 3; // Modes intro
  };

  // Progress indicator component
  const ProgressIndicator = () => (
    <div className="flex justify-center mb-4">
      <div className="flex items-center space-x-2">
        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-colors ${
          getProgressSegment() >= 1 ? 'bg-gray-800 text-white' : 'bg-gray-200 text-gray-500'
        }`}>
          1
        </div>
        <div className={`w-8 h-1 transition-colors ${getProgressSegment() >= 2 ? 'bg-gray-600' : 'bg-gray-200'}`}></div>
        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-colors ${
          getProgressSegment() >= 2 ? 'bg-gray-800 text-white' : 'bg-gray-200 text-gray-500'
        }`}>
          2
        </div>
        <div className={`w-8 h-1 transition-colors ${getProgressSegment() >= 3 ? 'bg-gray-600' : 'bg-gray-200'}`}></div>
        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-colors ${
          getProgressSegment() >= 3 ? 'bg-gray-800 text-white' : 'bg-gray-200 text-gray-500'
        }`}>
          3
        </div>
      </div>
    </div>
  );

  // Step 1: Welcome + Name
  const renderStep1 = () => (
    <Card className="w-full border-0 shadow-none sm:border sm:shadow-sm">
      <CardHeader className="text-center pt-8 pb-4">
        <div className="flex justify-center mb-6">
          <WelcomeLogo className="w-20 h-20 sm:w-24 sm:h-24" />
        </div>
        <CardTitle className="text-3xl sm:text-4xl font-title font-normal">Welcome to Higlo</CardTitle>
        {userType === 'invitedUser' && (
          <CardDescription className="text-base mt-2">
            You have been invited to join {familyName}
          </CardDescription>
        )}
      </CardHeader>
      <CardContent className="pb-8">
        <form onSubmit={handleStep1Submit} className="space-y-6">
          <div className="space-y-2">
            <label htmlFor="name" className="text-sm font-medium text-gray-700">
              What's your name?
            </label>
            <Input
              id="name"
              type="text"
              placeholder="Joe Smith"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="h-12 text-base"
              required
            />
          </div>

          <Button
            type="submit"
            className="w-full h-12 bg-gray-800 text-white hover:bg-gray-700 text-base font-medium"
            disabled={isLoading}
          >
            {isLoading ? 'Setting up...' :
             userType === 'invitedUser' ? 'Get Started' : 'Start Setup'}
          </Button>
        </form>
      </CardContent>
    </Card>
  );

  // Step 2: Hearth Intro
  const renderStep2 = () => (
    <Card className="w-full border-0 shadow-none sm:border sm:shadow-sm">
      <CardHeader className="text-center pt-6 pb-4">
        <div className="flex justify-center mb-4">
          <HearthIcon className="w-28 h-auto sm:w-32" />
        </div>
        <CardTitle className="text-3xl sm:text-4xl font-title font-normal">Your Hearth</CardTitle>
      </CardHeader>
      <CardContent className="pb-6 space-y-5">
        <div className="text-base text-gray-600 space-y-4 leading-relaxed">
          <p>
            Hearths are your portal to connect with family and friends. One sits at each home.
          </p>
          <p>
            You have the <span className="font-semibold text-gray-800">Primary Hearth</span>, and have access to everything like setup and configuration.
          </p>
          <p>
            Everybody else has an <span className="font-semibold text-gray-800">Additional Hearth</span> and doesn't have to worry about any of this.
          </p>
        </div>

        <div className="flex gap-3">
          <Button
            variant="outline"
            className="w-full h-12 text-base font-medium"
            onClick={() => setCurrentStep(1)}
          >
            Back
          </Button>
          <Button
            onClick={() => setCurrentStep(3)}
            className="w-full h-12 bg-gray-800 text-white hover:bg-gray-700 text-base font-medium"
          >
            Next
          </Button>
        </div>
      </CardContent>
    </Card>
  );

  // Step 3: Hearth Setup (name each household)
  const renderStep3 = () => (
    <Card className="w-full border-0 shadow-none sm:border sm:shadow-sm">
      <CardHeader className="text-center pt-6 pb-4">
        <CardTitle className="text-3xl sm:text-4xl font-title font-normal">Setup your Hearths</CardTitle>
        <CardDescription className="text-base mt-3 leading-relaxed">
          Give a name for each household with a Hearth. You can change these names later.
        </CardDescription>
      </CardHeader>
      <CardContent className="pb-6">
        <form onSubmit={handleStep3Submit} className="space-y-5">
          <div className="space-y-4">
            {Array.from({ length: numOfFamilies }, (_, index) => (
              <div key={index} className="space-y-2">
                <label htmlFor={`family-${index}`} className="text-sm font-medium text-gray-700 flex items-center gap-2">
                  {index === 0 ? (
                    <PrimaryHearthSymbol className="w-4 h-4" />
                  ) : (
                    <AdditionalHearthSymbol className="w-4 h-4" />
                  )}
                  {index === 0 ? 'Who is using the Primary Hearth (You)' : 'Who is using this additional Hearth'}
                </label>
                <p className="text-xs text-gray-500 ml-6">
                  {index === 0 ? "What others will see you as" : "What you'll see them as"}
                </p>
                <Input
                  id={`family-${index}`}
                  type="text"
                  placeholder={
                    index === 0 ? "Joe's Family" :
                    index === 1 ? "Grandma and Grandad" :
                    index === 2 ? "Aunt Sue" :
                    index === 3 ? "Uncle Frank" :
                    "Smith Family"
                  }
                  value={familyNames[index] || ''}
                  onChange={(e) => {
                    const newFamilyNames = [...familyNames];
                    newFamilyNames[index] = e.target.value;
                    setFamilyNames(newFamilyNames);
                  }}
                  className="h-12 text-base"
                  required
                />
              </div>
            ))}
          </div>

          <div className="flex gap-3">
            <Button
              type="button"
              variant="outline"
              className="w-full h-12 text-base font-medium"
              onClick={() => setCurrentStep(2)}
            >
              Back
            </Button>
            <Button
              type="submit"
              className="w-full h-12 bg-gray-800 text-white hover:bg-gray-700 text-base font-medium"
            >
              Next
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );

  // Step 4: Higlos Intro
  const renderStep4 = () => (
    <Card className="w-full border-0 shadow-none sm:border sm:shadow-sm">
      <CardHeader className="text-center pt-6 pb-4">
        <div className="flex justify-center mb-4">
          <img
            src={HigloGraphics}
            alt="Higlo spaces visualization"
            className="w-44 h-auto sm:w-52 opacity-90"
          />
        </div>
        <CardTitle className="text-3xl sm:text-4xl font-title font-normal">Your Higlos</CardTitle>
      </CardHeader>
      <CardContent className="pb-6 space-y-5">
        <div className="text-base text-gray-600 space-y-4 leading-relaxed">
          <p>
            Each Hearth connects into common shared spaces, called Higlos.
          </p>
          <p>
            You can have up to three Higlos for different groups of people, and enter them by yourself and with others.
          </p>
        </div>

        <div className="flex gap-3">
          <Button
            variant="outline"
            className="w-full h-12 text-base font-medium"
            onClick={() => setCurrentStep(3)}
          >
            Back
          </Button>
          <Button
            onClick={() => setCurrentStep(5)}
            className="w-full h-12 bg-gray-800 text-white hover:bg-gray-700 text-base font-medium"
          >
            Next
          </Button>
        </div>
      </CardContent>
    </Card>
  );

  // Step 5: Name First Higlo
  const renderStep5 = () => (
    <Card className="w-full border-0 shadow-none sm:border sm:shadow-sm">
      <CardHeader className="text-center pt-6 pb-4">
        <div className="flex justify-center mb-4">
          <img
            src={HigloGraphics}
            alt="Higlo spaces visualization"
            className="w-44 h-auto sm:w-52 opacity-90"
          />
        </div>
        <CardTitle className="text-3xl sm:text-4xl font-title font-normal">Name your first Higlo</CardTitle>
        <CardDescription className="text-base mt-3 leading-relaxed">
          We have set up your first Higlo where all your Hearths are members of. This is where everybody will meet for the first time.
        </CardDescription>
      </CardHeader>
      <CardContent className="pb-6">
        <form onSubmit={handleStep5Submit} className="space-y-5">
          <div className="space-y-2">
            <label htmlFor="iglooName" className="text-sm font-medium text-gray-700">
              Give this a meaningful name. You can change this later.
            </label>
            <Input
              id="iglooName"
              type="text"
              placeholder="Family"
              value={iglooName}
              onChange={(e) => setIglooName(e.target.value)}
              className="h-12 text-base"
              required
            />
          </div>

          <div className="flex gap-3">
            <Button
              type="button"
              variant="outline"
              className="w-full h-12 text-base font-medium"
              onClick={() => setCurrentStep(4)}
            >
              Back
            </Button>
            <Button
              type="submit"
              className="w-full h-12 bg-gray-800 text-white hover:bg-gray-700 text-base font-medium"
            >
              Next
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );

  // Step 6: Modes Intro (Navigating spaces)
  const renderStep6 = () => (
    <Card className="w-full border-0 shadow-none sm:border sm:shadow-sm">
      <CardHeader className="text-center pt-6 pb-2">
        <div className="flex justify-center mb-3">
          <HomeSymbol className="w-10 h-10" />
        </div>
        <CardTitle className="text-3xl sm:text-4xl font-title font-normal">Navigating spaces</CardTitle>
      </CardHeader>
      <CardContent className="pb-6 space-y-4">
        <div className="text-base text-gray-600 space-y-3 leading-relaxed">
          <p>
            We have made it faster for homes with a single Higlo with <span className="font-semibold text-gray-800">Auto Mode</span>.
          </p>
          <p>
            Apart from your primary Hearth, the others have been set in Auto Mode. You can change this anytime.
          </p>
        </div>

        <div className="bg-gray-50 rounded-lg p-3 space-y-3">
          <div className="flex items-start gap-3">
            <AutoModeSymbol className="w-6 h-6 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-gray-600"><span className="font-semibold text-gray-900">Auto mode:</span> You automatically go to your only Higlo as soon as you open the lid.</p>
          </div>
          <div className="flex items-start gap-3">
            <NormalModeSymbol className="w-6 h-auto flex-shrink-0 mt-1" />
            <p className="text-sm text-gray-600"><span className="font-semibold text-gray-900">Normal mode:</span> You choose which Higlo to go into each time you start your Hearth.</p>
          </div>
        </div>

        <div className="flex gap-3">
          <Button
            variant="outline"
            className="w-full h-12 text-base font-medium"
            onClick={() => setCurrentStep(5)}
            disabled={isLoading}
          >
            Back
          </Button>
          <Button
            onClick={handleFinalSubmit}
            className="w-full h-12 bg-gray-800 text-white hover:bg-gray-700 text-base font-medium"
            disabled={isLoading}
          >
            {isLoading ? 'Setting up...' : 'Finish'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );

  const renderCurrentStep = () => {
    switch (currentStep) {
      case 1:
        return renderStep1();
      case 2:
        return renderStep2();
      case 3:
        return renderStep3();
      case 4:
        return renderStep4();
      case 5:
        return renderStep5();
      case 6:
        return renderStep6();
      default:
        return renderStep1();
    }
  };

  return (
    <div className="min-h-[100dvh] bg-gray-50 flex flex-col">
      {/* Header with sign out */}
      <div className="flex justify-end p-4">
        <button
          onClick={handleSignOut}
          className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 transition-colors"
        >
          <LogOut className="h-4 w-4" />
          Sign out
        </button>
      </div>

      {/* Main content - top-aligned on both mobile and desktop */}
      <div className="flex-1 flex flex-col px-4 pb-6 sm:px-6">
        <div className="w-full max-w-md mx-auto">
          {/* Progress indicator - only show for hearthOwner after step 1 */}
          {userType === 'hearthOwner' && currentStep > 1 && (
            <ProgressIndicator />
          )}

          {renderCurrentStep()}
        </div>
      </div>

      {/* Error Modal */}
      <ErrorModal
        isOpen={showErrorModal}
        message={errorMessage}
        title="Setup Error"
        onClose={closeErrorModal}
      />
    </div>
  );
}

export default AccountSetup;
